// ────────────────────────────────────────────────────────────
// TRACT PANELS — queue · worksheet · flags · parties · log
// ────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { TractBundle, FlagGroup, FlagItem, TractParty, PartyResolution, PartyCorpusDoc, EstateStep } from '../types';
import type { TractRequestsState } from '../data/requests';
import { partyCorpusDocs, convertToDeal, confirmIdentity } from '../data/parties';
import { aliasVariants } from '../data/derive';

// ─── ingest request queue ───────────────────────────────────

export function RequestsPanel({ requests }: { requests: TractRequestsState }) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const valid = /^\d{4,9}$/.test(draft.trim());

  const onQueue = async () => {
    if (!valid || busy) return;
    setBusy(true);
    try {
      await requests.queueDoc(draft.trim());
      setDraft('');
    } catch { /* error shown below */ }
    setBusy(false);
  };

  const items = [
    ...requests.ingest.map(r => ({
      id: r.id, label: r.doc_number, kind: 'doc' as const, status: r.status, note: r.note,
    })),
    ...requests.research.map(t => ({
      id: t.id, label: `trace: ${t.subject}`, kind: 'trace' as const, status: t.status, note: null as string | null,
    })),
  ];

  return (
    <div className="dd-card">
      <div className="dd-card-head">
        <div className="dd-card-title">Request <em>ingest</em></div>
        <div className="dd-card-meta">pipeline worklist</div>
      </div>
      <div className="dd-requests">
        <div className="dd-request-inputrow">
          <input
            className="dd-request-input"
            type="text"
            inputMode="numeric"
            placeholder="Paste a Weld doc number…"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void onQueue(); }}
          />
          <button className="dd-doc-btn" onClick={() => void onQueue()} disabled={!valid || busy}>
            {busy ? 'Queueing…' : 'Queue'}
          </button>
        </div>
        {!requests.available && (
          <div className="dd-request-unavailable">
            Queue unreachable — local dev without Functions, or network. Requests still work on the deployed site.
          </div>
        )}
        {requests.error && <div className="dd-request-error">{requests.error}</div>}
        {items.length > 0 && (
          <div className="dd-request-chips">
            {items.map(i => (
              <span key={i.id} className={`dd-request-chip ${i.status} ${i.kind}`} title={i.note ?? undefined}>
                {i.kind === 'doc' ? '⎘ ' : '⑂ '}{i.label} · {i.status === 'pending' ? 'queued' : i.status}
              </span>
            ))}
          </div>
        )}
        <div className="dd-request-note">
          We fetch, interpret, and gate through the pipeline; documents never enter from this page.
          Docs without a Weld number go to the manual R2 inbox (link in the header).
        </div>
      </div>
    </div>
  );
}

// ─── NMA worksheet ──────────────────────────────────────────

export function WorksheetPanel({ tract, estatePath }: { tract: TractBundle; estatePath?: EstateStep[] }) {
  const { worksheet } = tract;
  if (worksheet.rows.length === 0) return null;

  // The estate-share row is where a multi-generation probate path collapses
  // into one composite fraction ("1/45"). When we have the subject's estate
  // path (deal lens), expand it into visible doc-cited fractures right before
  // that row so the jump from "40 ac" to "1/45" stops hiding the descent.
  const estateRowIdx = worksheet.rows.findIndex(r => /share of the estate/i.test(r.label));
  const showPath = !!estatePath && estatePath.length > 0 && estateRowIdx >= 0;

  return (
    <div className="dd-card">
      <div className="dd-card-head">
        <div className="dd-card-title">NMA <em>worksheet</em></div>
        <div className="dd-card-meta">each factor cites its doc</div>
      </div>
      <div className="dd-math-rows">
        {worksheet.rows.map((r, i) => (
          <div key={`grp-${i}`} style={{ display: 'contents' }}>
            {showPath && i === estateRowIdx && (
              <EstatePathRows steps={estatePath!} />
            )}
            <div className={`dd-math-row ${r.kind}`}>
              <span className="label">{r.label}</span>
              <span className="value">{r.value}</span>
              <span className="source">{r.source}</span>
            </div>
          </div>
        ))}
      </div>
      {showPath && (
        <div className="dd-estate-note">
          Estate shares are per stirpes (unequal by branch). The composite
          fraction above is the agent's; exact per-instrument shares are not in
          the extract.
        </div>
      )}
      <div className="dd-math-result">{worksheet.result}</div>
      {worksheet.reconciliation && (
        <details className="dd-math-recon">
          <summary>Reconciliation trace</summary>
          <pre>{worksheet.reconciliation.split('; ').join(';\n')}</pre>
        </details>
      )}
    </div>
  );
}

function EstatePathRows({ steps }: { steps: EstateStep[] }) {
  return (
    <>
      {steps.map((s, i) => (
        <div key={`estate-${i}`} className="dd-math-row estate-step">
          <span className="label">
            ⑃ through the Estate of {s.decedent}
            {s.subjectIsGrantee
              ? ` · subject is 1 of ${s.granteeCount} heirs`
              : ` · ${s.granteeCount} heirs`}
          </span>
          <span className="value">per stirpes</span>
          <span className="source">{s.docNumber ? `doc ${s.docNumber}` : 'distribution'}</span>
        </div>
      ))}
    </>
  );
}

// ─── derived-vs-stored cross-checks ─────────────────────────

export function MathNotesPanel({ tract }: { tract: TractBundle }) {
  if (tract.mathNotes.length === 0) return null;
  return (
    <div className="dd-card dd-mathnotes">
      <div className="dd-card-head">
        <div className="dd-card-title">Derivation <em>cross-checks</em></div>
        <div className="dd-card-meta">{tract.mathNotes.length} disagreement{tract.mathNotes.length === 1 ? '' : 's'}</div>
      </div>
      <div className="dd-mathnotes-body">
        {tract.mathNotes.map((n, i) => <div key={i} className="dd-mathnote">{n}</div>)}
      </div>
    </div>
  );
}

// ─── flags ──────────────────────────────────────────────────

const GROUP_TITLE: Record<FlagGroup, string> = {
  critical: 'Critical',
  pricing: 'Pricing ambiguity',
  gap: 'Possible chain gaps',
  ops: 'Operational',
  other: 'Other',
};
const GROUP_ORDER: FlagGroup[] = ['critical', 'pricing', 'gap', 'ops', 'other'];

export function FlagsPanel({ tract, onJumpToDoc }: {
  tract: TractBundle;
  onJumpToDoc: (docNumber: string) => void;
}) {
  const groups = GROUP_ORDER
    .map(g => ({ g, items: tract.flags.filter(f => f.group === g) }))
    .filter(x => x.items.length > 0);
  if (groups.length === 0) return null;

  return (
    <div className="dd-card">
      <div className="dd-card-head">
        <div className="dd-card-title">Flags</div>
        <div className="dd-card-meta">{tract.flags.length} open</div>
      </div>
      {groups.map(({ g, items }) => (
        <div key={g} className={`dd-flag-group ${g}`}>
          <div className="dd-flag-group-title">
            {GROUP_TITLE[g]} <span className="count">· {items.length}</span>
          </div>
          {items.map((f: FlagItem) => (
            <div key={f.id} className="dd-flag">
              {f.text}
              {f.linkedDocNumber && (
                <button className="dd-flag-jump" onClick={() => onJumpToDoc(f.linkedDocNumber!)}>
                  → view event {f.linkedDocNumber}
                </button>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ─── parties: traced rows w/ actions + untraced branches ────

export interface PartiesPanelProps {
  tract: TractBundle;
  requests: TractRequestsState;
  /** matchKey → identity resolution (null while loading; absent key = unresolved) */
  resolutions: Map<string, PartyResolution> | null;
  identityAvailable: boolean;
  onSkipTrace: (party: TractParty) => void;
  onConverted: () => void;
  /** Patch one resolution in place after an explicit confirm click. */
  onPatchResolution: (matchKey: string, patch: Partial<PartyResolution>) => void;
}

export function PartiesPanel({
  tract, requests, resolutions, identityAvailable, onSkipTrace, onConverted, onPatchResolution,
}: PartiesPanelProps) {
  const { parties, untraced, untracedTotal, tracedTotal, grossAcres, confUniform } = tract;
  const [queueing, setQueueing] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [converting, setConverting] = useState<string | null>(null);
  const [convertNote, setConvertNote] = useState<string | null>(null);

  // Ordered as a call list, best classification first: proven sellers with
  // remaining inventory (SOLD here · HOLDS elsewhere) beat never-sold holders,
  // which beat clean exits; entities (aggregators) last. Re-sorts once
  // resolutions arrive (holds-elsewhere is resolved server-side).
  const orderedParties = useMemo(() => {
    const rank = (p: TractParty): number => {
      if (p.kind === 'competitor') return 3;                                   // entities last
      if (p.disposition === 'sold') return resolutions?.get(p.matchKey)?.holdsElsewhere ? 0 : 2;
      return 1;                                                                // likely holds
    };
    return [...parties].sort((a, b) => rank(a) - rank(b) || b.nma - a.nma);
  }, [parties, resolutions]);

  const pendingTraces = new Set(
    requests.research.filter(t => t.status === 'pending').map(t => t.subject),
  );

  const onTraceBranch = async (name: string, detail: string) => {
    setQueueing(name);
    try { await requests.queueBranchTrace(name, detail); } catch { /* surfaced in queue panel */ }
    setQueueing(null);
  };

  const onConvert = async (p: TractParty) => {
    setConverting(p.matchKey);
    setConvertNote(null);
    try {
      const r = await convertToDeal({ trs: tract.trs, county: tract.county, subjectParty: p.name });
      setConvertNote(r.duplicate
        ? `${p.name} already has an open deal (${r.deal.ref}).`
        : `Created ${r.deal.ref} for ${p.name}.`);
      onConverted();
    } catch (e) {
      setConvertNote(`Convert failed: ${(e as Error).message}`);
    }
    setConverting(null);
  };

  // Only claim the quarter is fully accounted for when there IS a quarter.
  const accounted = grossAcres > 0 && Math.abs(tracedTotal + untracedTotal - grossAcres) < 0.01;

  // A tract with no disposition at all (no theory yet) has nothing to say.
  if (parties.length === 0 && untraced.length === 0) return null;

  return (
    <div className="dd-card">
      <div className="dd-card-head">
        <div className="dd-card-title">Ownership <em>disposition</em></div>
        <div className="dd-card-meta">derived from chain</div>
      </div>

      {untracedTotal > 0 && (
        <div className="dd-untraced-banner">
          <span className="n">{fmtAc(untracedTotal)} of {fmtAc(grossAcres)} ac</span>
          {' '}sits in untraced branches
        </div>
      )}

      {confUniform && (
        <div className="dd-conf-banner">
          All figures {confUniform} confidence · chain incomplete caps everything
        </div>
      )}

      {convertNote && <div className="dd-convert-note">{convertNote}</div>}

      <div className="dd-owners">
        <div className="dd-owners-section">Traced to living owners · {parties.length} <span className="dd-owners-hint">· call top-down</span></div>
        {orderedParties.map(p => {
          const res = resolutions?.get(p.matchKey);
          const isOpen = expanded === p.matchKey;
          return (
            <div key={p.matchKey}>
              <div className={`dd-owner ${p.kind} ${p.disposition === 'sold' ? 'sold-out' : ''}`}>
                <span className="name-cell">
                  <button
                    className={`dd-expand-arrow ${isOpen ? 'open' : ''}`}
                    onClick={() => setExpanded(isOpen ? null : p.matchKey)}
                    title="Show this party's MI-indexed docs and leases"
                  >▸</button>
                  <span className="name" title={p.name}>{p.name}</span>
                  {res?.grade === 'firm' && res.persisted && (
                    <span className="dd-id-dot" title={`Linked to MI party ${res.displayName} · alias recorded`} />
                  )}
                  {res?.grade === 'firm' && !res.persisted && (
                    <span className="dd-id-dot unrecorded" title={`Firm match (token-permuted exact): ${res.displayName} — not yet recorded; confirm in the expand`} />
                  )}
                  {res?.grade === 'probable' && (
                    <span className="dd-id-dot probable" title={`Probable MI match (name + tract corroborated): ${res.displayName} — not linked`} />
                  )}
                </span>
                <span className="nma">{p.nma.toFixed(4)}</span>
                <span className="row-actions">
                  <button
                    className="dd-row-btn"
                    onClick={() => onSkipTrace(p)}
                    title="Hand this party to the recorder skip-trace agent (MI flow)"
                  >Skip trace</button>
                  <button
                    className="dd-row-btn convert"
                    disabled={converting === p.matchKey}
                    onClick={() => void onConvert(p)}
                    title="Create a thin deal record pointing at this tract"
                  >{converting === p.matchKey ? '…' : 'Convert to deal'}</button>
                </span>
                {/* Disposition on its own full-width line so long "SOLD → …"
                    badges are fully legible and never collide with the NMA. */}
                <span className="dd-disp-line">
                  {p.disposition === 'sold'
                    ? <span className="dd-disp sold" title={p.soldDoc ? `Conveyed out in doc ${p.soldDoc}` : undefined}>SOLD&nbsp;HERE&nbsp;→&nbsp;{p.soldTo}</span>
                    : p.kind === 'individual'
                      ? <span className="dd-disp holds" title="No conveyance out on record — acquisition target">LIKELY HOLDS</span>
                      : <span className="dd-disp comp-holds" title="Holds; competitor/aggregator, not an acquisition target">HOLDS</span>}
                  {p.disposition === 'sold' && res?.holdsElsewhere && (
                    <span
                      className="dd-disp holds-elsewhere"
                      title={`Proven seller with remaining inventory — likely current holdings on ${[...res.holdsElsewhere.tracts, res.holdsElsewhere.more ? `+${res.holdsElsewhere.more} more` : ''].filter(Boolean).join(', ')}. Evidence in the expand.`}
                    >
                      HOLDS {res.holdsElsewhere.tracts.join(', ')}{res.holdsElsewhere.more > 0 ? ` +${res.holdsElsewhere.more}` : ''}
                    </span>
                  )}
                </span>
                <span className="bar"><div style={{ width: `${Math.max(p.share * 100, 0.75)}%` }} /></span>
              </div>
              {isOpen && (
                <PartyExpand
                  party={p}
                  resolution={res ?? null}
                  identityAvailable={identityAvailable}
                  resolving={resolutions === null}
                  onPatched={patch => onPatchResolution(p.matchKey, patch)}
                />
              )}
            </div>
          );
        })}

        {untraced.length > 0 && (
          <div className="dd-owners-section untraced">Untraced branches · {untraced.length}</div>
        )}
        {untraced.map((u, i) => {
          const detail = `${fmtAc(u.acres)} ac ${u.side} at ${u.docNumber ?? '?'} · untraced since ${u.sinceYear}`;
          const running = pendingTraces.has(u.name);
          return (
            <div key={i} className="dd-owner dd-untraced-row">
              <span className="name" title={detail}>
                {u.name}
                <span className="dd-untraced-since"> · untraced since {u.sinceYear}</span>
              </span>
              <span className="nma">{u.acres.toFixed(4)}</span>
              <span className="row-actions">
                {running
                  ? <span className="dd-request-chip pending trace">trace running</span>
                  : (
                    <button
                      className="dd-trace-btn"
                      disabled={queueing === u.name}
                      onClick={() => void onTraceBranch(u.name, detail)}
                      title="Queue a branch-trace research task for the pipeline"
                    >
                      {queueing === u.name ? '…' : '→ trace'}
                    </button>
                  )}
              </span>
              <span className="bar"><div style={{ width: `${Math.max((u.acres / grossAcres) * 100, 0.75)}%` }} /></span>
            </div>
          );
        })}

        <div className="dd-owners-total">
          <span>
            Traced {fmtAc(tracedTotal)} + untraced {fmtAc(untracedTotal)}
            {accounted && <span className="ok"> ✓ accounts for the full {fmtAc(grossAcres)} ac</span>}
          </span>
          <span className="n">{fmtAc(tracedTotal + untracedTotal)} ac</span>
        </div>
      </div>
    </div>
  );
}

// ─── inline party expand: MI corpus join ────────────────────

function PartyExpand({ party, resolution, identityAvailable, resolving, onPatched }: {
  party: TractParty;
  resolution: PartyResolution | null;
  identityAvailable: boolean;
  resolving: boolean;
  onPatched: (patch: Partial<PartyResolution>) => void;
}) {
  const [docs, setDocs] = useState<PartyCorpusDoc[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Fetch the corpus join once the identity resolution is in hand.
  const partyId = resolution?.partyId && !resolution.tentative ? resolution.partyId : null;
  useEffect(() => {
    if (!partyId) return;
    let alive = true;
    partyCorpusDocs(partyId)
      .then(r => { if (alive) setDocs(r.documents); })
      .catch(e => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, [partyId]);

  return (
    <div className="dd-party-expand">
      {party.disposition === 'sold' && resolution?.holdsElsewhere && (
        <div className="dd-holds-elsewhere">
          <div className="dd-he-tag">
            Proven seller · likely holds elsewhere ·{' '}
            {resolution.holdsElsewhere.tracts.join(', ')}
            {resolution.holdsElsewhere.more > 0 ? ` +${resolution.holdsElsewhere.more} more` : ''}
          </div>
          <div className="dd-expand-docs">
            {resolution.holdsElsewhere.docs.map((d, i) => (
              <div key={i} className="dd-expand-doc">
                <a className="doc" href={`/mi/document/${d.docNumber}`} target="_blank" rel="noreferrer">{d.docNumber}</a>
                {d.role && <span className={`role ${d.role}`}>{d.role}</span>}
                <span className="type">{d.docType ?? '—'}</span>
                <span className="trs">{d.trsKey}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {!identityAvailable && (
        <div className="dd-expand-note">
          Identity layer unreachable (local dev or network). On the deployed site this
          resolves against the MI corpus.
        </div>
      )}
      {identityAvailable && resolving && (
        <div className="dd-expand-note">Resolving against MI parties…</div>
      )}
      {identityAvailable && !resolving && !resolution?.partyId && (
        <div className="dd-expand-note">
          No MI corpus match for this name. Chain docs here: {party.chainDocNumbers.join(', ') || 'none'}.
        </div>
      )}
      {identityAvailable && resolution?.grade === 'probable' && resolution.corroboration && (
        <div className="dd-expand-probable">
          <div className="dd-probable-tag">Probable · name + tract corroborated</div>
          <div className="dd-expand-head" style={{ marginBottom: 4 }}>
            MI candidate: <strong>{resolution.displayName}</strong>
            <span className="via">trigram + tract evidence</span>
            {resolution.partyId && (
              <a className="dd-mi-link" href={`/mi/party/${resolution.partyId}`} target="_blank" rel="noreferrer">
                open in MI ↗
              </a>
            )}
          </div>
          <div className="dd-probable-summary">{resolution.corroboration.summary}</div>
          <div className="dd-expand-docs">
            {resolution.corroboration.docs.map((d, i) => (
              <div key={i} className="dd-expand-doc">
                <a className="doc" href={`/mi/document/${d.docNumber}`} target="_blank" rel="noreferrer">
                  {d.docNumber}
                </a>
                <span className="type">{d.docType ?? '—'}</span>
                <span className="trs">{d.trsKey}</span>
              </div>
            ))}
          </div>
          <div className="dd-expand-note" style={{ marginTop: 6 }}>
            Same tract, same name is strong identity evidence — grading only: nothing is
            recorded until you confirm.
          </div>
          <ConfirmIdentity party={party} resolution={resolution} onPatched={onPatched} />
        </div>
      )}
      {identityAvailable && resolution?.grade === 'tentative' && (
        <div className="dd-expand-note">
          Tentative match only ({resolution.displayName}, name-only trigram — no instruments
          in or near this tract) — not linked, not persisted.
        </div>
      )}
      {resolution?.partyId && !resolution.tentative && (
        <>
          <div className="dd-expand-head">
            MI party: <strong>{resolution.displayName}</strong>
            <span className="via">
              {resolution.via === 'exact-permuted'
                ? 'token-permuted exact (recorder order)'
                : `matched via ${resolution.via}`}
            </span>
            <a className="dd-mi-link" href={`/mi/party/${resolution.partyId}`} target="_blank" rel="noreferrer">
              open in MI ↗
            </a>
          </div>
          {!resolution.persisted && (
            <ConfirmIdentity party={party} resolution={resolution} onPatched={onPatched} />
          )}
          {resolution.via === 'confirmed' && (
            <div className="dd-confirmed-note">✓ Identity confirmed · alias link recorded</div>
          )}
          {err && <div className="dd-expand-note">Docs unavailable: {err}</div>}
          {!err && docs === null && <div className="dd-expand-note">Loading corpus docs…</div>}
          {docs !== null && docs.length === 0 && (
            <div className="dd-expand-note">No indexed docs for this party yet.</div>
          )}
          {docs !== null && docs.length > 0 && (
            <div className="dd-expand-docs">
              {docs.map((d, i) => (
                <div key={i} className="dd-expand-doc">
                  <a className="doc" href={`/mi/document/${d.docNumber}`} target="_blank" rel="noreferrer">
                    {d.docNumber}
                  </a>
                  <span className={`role ${d.role}`}>{d.role}</span>
                  <span className="type">{d.docType ?? '—'}</span>
                  <span className="date">{d.recordingDate ?? '—'}</span>
                  <span className="trs">{d.trsLabel ?? ''}</span>
                  {d.lessee && <span className="lessee">→ {d.lessee}</span>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── explicit identity confirmation ─────────────────────────
// The append-only alias record grows ONLY here: a human reviewed the
// evidence and clicked. Match grading never writes.

function ConfirmIdentity({ party, resolution, onPatched }: {
  party: TractParty;
  resolution: PartyResolution;
  onPatched: (patch: Partial<PartyResolution>) => void;
}) {
  const [state, setState] = useState<'idle' | 'busy' | 'done' | 'error'>('idle');
  const [msg, setMsg] = useState<string | null>(null);

  const onConfirm = async () => {
    if (!resolution.partyId) return;
    setState('busy');
    try {
      const r = await confirmIdentity({
        partyId: resolution.partyId,
        name: party.name,
        aliases: aliasVariants(party.name),
      });
      setMsg(r.alreadyRecorded
        ? `Already recorded against ${r.displayName}.`
        : `Recorded ${r.inserted} alias form${r.inserted === 1 ? '' : 's'} against ${r.displayName}.`);
      setState('done');
      onPatched({ grade: 'firm', via: 'confirmed', tentative: false, persisted: true });
    } catch (e) {
      setMsg((e as Error).message);
      setState('error');
    }
  };

  if (state === 'done') {
    return <div className="dd-confirmed-note">✓ Identity confirmed · {msg}</div>;
  }
  return (
    <div className="dd-confirm-row">
      <button className="dd-confirm-btn" onClick={() => void onConfirm()} disabled={state === 'busy'}>
        {state === 'busy' ? 'Recording…' : '✓ Confirm identity'}
      </button>
      <span className="dd-confirm-hint">records the alias link · append-only, no merge</span>
      {state === 'error' && <span className="dd-request-error">{msg}</span>}
    </div>
  );
}

// ─── revision log ───────────────────────────────────────────

export function RevisionLogPanel({ tract }: { tract: TractBundle }) {
  if (tract.revisions.length === 0) return null;
  return (
    <div className="dd-card">
      <div className="dd-card-head">
        <div className="dd-card-title">Revision <em>log</em></div>
        <div className="dd-card-meta">{tract.revisions.length} number-moving promotions</div>
      </div>
      <div className="dd-revisions">
        {tract.revisions.map(r => (
          <div key={r.id} className="dd-revision">
            <span className="doc">{r.docNumber}</span>
            <span className="when">{r.occurredAt.slice(0, 10)}</span>
            <span className="change">{r.field}: {r.oldValue} → {r.newValue}</span>
            {r.reason && <span className="why">{r.reason}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── thin deals strip (tract page) ──────────────────────────

export function DealsStrip({ deals }: { deals: Array<{ id: string; ref: string; subjectParty: string; stage: string; closeUrl: string | null }> }) {
  if (deals.length === 0) {
    return (
      <div className="dd-deals-strip empty">
        No deals on this tract yet. Convert a traced party to create one.
      </div>
    );
  }
  return (
    <div className="dd-deals-strip">
      <span className="label">Deals on this tract · {deals.length}</span>
      {deals.map(d => (
        <Link key={d.id} to={`/deal-desk/deals/${d.id}`} className="dd-deal-chip">
          <span className="ref">{d.ref}</span>
          <span className="who">{d.subjectParty}</span>
          <span className="stage">{d.stage}</span>
        </Link>
      ))}
    </div>
  );
}

function fmtAc(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
}
